import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import {
  geolocationPermission,
  getDevicePosition,
  isGeolocationAvailable,
  readCurrentLocationPreference,
  writeCurrentLocationPreference,
} from '../../../util/deviceLocation';
import { InlineTextButton } from '../../../components';

import css from './CurrentLocationToggle.module.css';

const IconPin = () => (
  <svg className={css.icon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="none">
    <path
      d="M12 21s-7-5.1-7-11a7 7 0 1 1 14 0c0 5.9-7 11-7 11z"
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.5" fill="none" strokeWidth="2" />
  </svg>
);

/**
 * "Distances from: your home address · Use my current location" above the search results. Turning
 * current location on asks the browser for permission once; the choice is remembered (see
 * src/util/deviceLocation.js), so later visits re-read the position without a prompt. Reports the
 * device origin ({ lat, lng }, or null when off) through `onChange`. Client-only: renders nothing
 * where the Geolocation API is missing (and during SSR).
 *
 * @param {Object} props
 * @param {Function} props.onChange (origin|null) => void
 * @param {boolean} props.hasHomeLocation the parent has a saved home location to fall back to
 * @param {string} [props.searchedAddress] the place typed into search: distances are from there
 * @param {string} [props.className]
 */
const CurrentLocationToggle = ({ onChange, hasHomeLocation, searchedAddress, className }) => {
  // 'off' | 'locating' | 'on' | 'denied' | 'unavailable'
  const [status, setStatus] = useState('off');
  const [available, setAvailable] = useState(false);

  const locate = () => {
    setStatus('locating');
    return getDevicePosition().then(
      origin => {
        writeCurrentLocationPreference(true);
        setStatus('on');
        onChange(origin);
      },
      err => {
        writeCurrentLocationPreference(false);
        setStatus(err?.code === 'denied' ? 'denied' : 'unavailable');
        onChange(null);
      }
    );
  };

  // Remembered opt-in: re-read the position on a later visit -- silently when the browser already
  // grants it. A revoked permission drops the opt-in instead of prompting unasked.
  useEffect(() => {
    const isAvailable = isGeolocationAvailable();
    setAvailable(isAvailable);
    if (!isAvailable || !readCurrentLocationPreference()) {
      return;
    }
    geolocationPermission().then(permission => {
      if (permission === 'denied') {
        writeCurrentLocationPreference(false);
        setStatus('denied');
      } else if (permission === 'granted') {
        // Only re-read silently when the browser already grants it: with the Permissions API
        // missing ('unknown' -- older Safari, Firefox without a remembered decision) a read would
        // show the location prompt on page load, unasked. The parent can tap the button again.
        locate();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!available && !searchedAddress) {
    return null;
  }

  // A place typed into search wins (distances match the "nearest" sort): just say so.
  if (searchedAddress) {
    return (
      <div className={classNames(css.root, className)}>
        <IconPin />
        <span className={css.from}>
          <FormattedMessage
            id="CurrentLocationToggle.fromSearched"
            values={{ place: searchedAddress }}
          />
        </span>
      </div>
    );
  }

  const turnOff = () => {
    writeCurrentLocationPreference(false);
    setStatus('off');
    onChange(null);
  };

  const fromLabel =
    status === 'on' ? (
      <FormattedMessage id="CurrentLocationToggle.fromCurrent" />
    ) : hasHomeLocation ? (
      <FormattedMessage id="CurrentLocationToggle.fromHome" />
    ) : null;

  return (
    <div className={classNames(css.root, className)} aria-live="polite">
      <IconPin />
      {fromLabel ? <span className={css.from}>{fromLabel}</span> : null}
      {status === 'on' ? (
        hasHomeLocation ? (
          <InlineTextButton type="button" className={css.action} onClick={turnOff}>
            <FormattedMessage id="CurrentLocationToggle.useHome" />
          </InlineTextButton>
        ) : (
          <InlineTextButton type="button" className={css.action} onClick={turnOff}>
            <FormattedMessage id="CurrentLocationToggle.turnOff" />
          </InlineTextButton>
        )
      ) : status === 'locating' ? (
        <span className={css.note}>
          <FormattedMessage id="CurrentLocationToggle.locating" />
        </span>
      ) : (
        <InlineTextButton type="button" className={css.action} onClick={locate}>
          <FormattedMessage id="CurrentLocationToggle.useCurrent" />
        </InlineTextButton>
      )}
      {status === 'denied' ? (
        <span className={css.note}>
          <FormattedMessage id="CurrentLocationToggle.denied" />
        </span>
      ) : status === 'unavailable' ? (
        <span className={css.note}>
          <FormattedMessage id="CurrentLocationToggle.unavailable" />
        </span>
      ) : null}
    </div>
  );
};

export default CurrentLocationToggle;
